import { Box } from "@mui/material";
import React from "react";
import { ColorSystemProps, LEGAL_TEXTS } from "../../../constants";
import { EskyPriceData, EskyPriceItem } from "../../../types";
import { numberToLocalString } from "../../../utils";
import { ColorIcon } from "../../common";

// 장례 흐름 순서. 여기 없는 대분류는 맨 뒤에 붙는다
const GROUP_ORDER = [
  "식사",
  "음료",
  "주류",
  "제사음식",
  "제단",
  "영정사진",
  "조화/화환/헌화",
  "관",
  "수의",
  "유골함",
  "입관용품",
  "상주용품",
  "기타용품",
  "운구차량",
  "도우미",
  "주차비",
  "전화비",
  "기타",
];

// 품목 단위가 고른 대분류만 평균을 보여준다. 식사는 1인분과 1통이 섞여 있어 평균이 왜곡된다
const AVERAGE_GROUPS = new Set(["관", "수의", "유골함", "제단", "운구차량"]);

const toMan = (won: number) =>
  won >= 10000 ? `${numberToLocalString(Math.round(won / 10000))}만원` : `${numberToLocalString(won)}원`;

const formatDate = (date: string) => {
  const [y, m, d] = date.split("-");
  return `${y}년 ${Number(m)}월 ${Number(d)}일`;
};

export function EskyPriceDetailSection({
  companyName,
  data,
  mainColor,
}: {
  companyName: string;
  data: EskyPriceData;
  mainColor: ColorSystemProps;
}) {
  const groups = new Map<string, EskyPriceItem[]>();
  data.items.forEach((item) => groups.set(item.group, [...(groups.get(item.group) || []), item]));
  const ordered = [...groups.entries()].sort(
    ([a], [b]) => (GROUP_ORDER.indexOf(a) + 1 || 99) - (GROUP_ORDER.indexOf(b) + 1 || 99)
  );

  return (
    <Box component="section" style={{ padding: "40px 20px" }}>
      <Box style={{ maxWidth: "720px", margin: "0 auto" }}>
        <Box component="h2" style={{ fontSize: "22px", fontWeight: 700, color: "#252525", margin: 0, lineHeight: 1.4 }}>
          {companyName}
          <br />
          식사, 장례용품 비용
        </Box>
        <Box component="p" style={{ margin: "8px 0 20px", fontSize: "13px", color: "#686868", lineHeight: 1.6 }}>
          {/*
            기준일은 장례식장이 등록한 날(price_date)이 아니라 우리가 e하늘에서 받은 날이다.
            등록일은 2025년 9월처럼 오래된 곳이 많아 최신 정보가 오래돼 보였다. 받은 날은 사실이고,
            다시 수집하면 갱신된다. 빌드 날짜는 쓰지 않는다 (매 빌드 전 페이지가 같은 날짜가 됨)
          */}
          e하늘 장사정보시스템에서 <time dateTime={data.collected_at}>{formatDate(data.collected_at)}</time>{" "}
          확인한 가격을 고이장례연구소가 항목별로 정리했어요. 총 {data.items.length}개 품목입니다.
        </Box>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebPage",
              name: `${companyName} 식사, 장례용품 비용`,
              dateModified: data.collected_at,
            }),
          }}
        />

        {ordered.map(([group, items]) => {
          const prices = items.map((i) => i.price);
          const subs = [...new Set(items.map((i) => i.sub))];
          return (
            <Box
              key={group}
              component="details"
              style={{ borderTop: "1px solid #e5e5e5", padding: "4px 0" }}
              sx={{
                "& summary::-webkit-details-marker": { display: "none" },
                "& .esky-close": { display: "none" },
                "&[open] .esky-open": { display: "none" },
                "&[open] .esky-close": { display: "inline" },
                "& .esky-chevron": { transform: "rotate(90deg)", transition: "transform 0.2s" },
                "&[open] .esky-chevron": { transform: "rotate(-90deg)" },
              }}
            >
              <Box
                component="summary"
                style={{ cursor: "pointer", padding: "14px 0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", listStyle: "none" }}
              >
                <span>
                  <span style={{ display: "block", fontSize: "16px", fontWeight: 700, color: "#252525" }}>{group}</span>
                  <span style={{ display: "block", marginTop: "2px", fontSize: "13px", color: "#888" }}>
                    {Math.min(...prices) === Math.max(...prices)
                      ? toMan(prices[0])
                      : `품목별 ${toMan(Math.min(...prices))} ~ ${toMan(Math.max(...prices))}`}
                  </span>
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: "2px", fontSize: "14px", fontWeight: 600, color: mainColor.c500, whiteSpace: "nowrap" }}>
                  <span className="esky-open">{items.length}개 품목 펼쳐보기</span>
                  <span className="esky-close">접기</span>
                  <ColorIcon name="arrow_right_small" color={mainColor.c500} size={18} className="esky-chevron" />
                </span>
              </Box>

              {subs.map((sub) => {
                const average = AVERAGE_GROUPS.has(group) ? data.averages[sub ? `${group}/${sub}` : group] : undefined;
                return (
                  <Box key={sub} style={{ marginBottom: "16px" }}>
                    {(sub || average) && (
                      <Box style={{ display: "flex", justifyContent: "space-between", gap: "8px", padding: "8px 12px", background: mainColor.c100, borderRadius: "8px", fontSize: "13px", color: "#535353" }}>
                        <span style={{ fontWeight: 600 }}>{sub || group}</span>
                        {average?.local ? (
                          <span>
                            관내 평균 {toMan(average.local)}
                            {average.national ? `, 전국 평균 ${toMan(average.national)}` : ""}
                          </span>
                        ) : null}
                      </Box>
                    )}
                    <Box component="table" style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                      <tbody>
                        {items
                          .filter((i) => i.sub === sub)
                          .map((i, idx) => (
                            <tr key={`${i.name}-${idx}`} style={{ borderBottom: "1px solid #f2f2f2" }}>
                              <td style={{ padding: "10px 12px", color: "#252525" }}>
                                {i.name || sub || group}
                                {i.note && (
                                  <span style={{ display: "block", marginTop: "2px", fontSize: "12px", color: "#888" }}>{i.note}</span>
                                )}
                              </td>
                              <td style={{ padding: "10px 12px", textAlign: "right", whiteSpace: "nowrap", fontWeight: 600, color: "#252525" }}>
                                {numberToLocalString(i.price)}원
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          );
        })}

        <Box style={{ borderTop: "1px solid #e5e5e5", paddingTop: "12px", fontSize: "11px", color: "#888", whiteSpace: "pre-line" }}>
          {LEGAL_TEXTS.COST_VARIABILITY}
        </Box>
      </Box>
    </Box>
  );
}
